/**
 * Pokémon GO top-panel OCR parser (Push 8 / v1.4.3 b8).
 *
 * The Pokémon GO Pokédex shows a trusted summary panel at the very top of the
 * screen. It has two states:
 *
 *   1. Compact / summary:   `KANTO   151 / 151`
 *   2. Expanded stats:      `KANTO   151 / 151`
 *                           `138 SHINY   150 LUCKY   99 XXL   91 XXS   72 100%`
 *
 * This panel is the user-visible source of truth. It tells us (a) which region
 * is on screen — overriding any stale region guess like HISUI/Paldea — and
 * (b) how many of the main Dex the trainer has caught. The grid sweep (which
 * only sees 4–6 rows per frame) is slow and error-prone; the top panel lets us
 * set expectations: if it says 151/151 we can mark the region complete without
 * scrolling, and if it says 148/151 we know to *find the missing 3* rather than
 * rebuild the region from zero.
 *
 * This module is pure and string-driven so it is trivially unit-testable. The
 * image-cropping/OCR layer (pokedexOcr.extractPokedexFrame) feeds the recognised
 * text in; we never touch the DOM here.
 *
 * Variant counts (shiny/lucky/xxl/xxs/perfect) are reported as *summary
 * telemetry only*. We deliberately do NOT bulk-mark shiny/lucky/etc. from a
 * single count — individual identities are unknown, so a count is an
 * expectation, not a list of registrations.
 */

import type { PokedexRegionLite, CellState } from "./pogoPokedexVariantClassifier";

// ─── Region size + national-dex range metadata ──────────────────────────────
// Mirrors REGIONS in types.ts. Kept local so this module has no runtime import
// of the wider type registry; topPanelOcr.test.ts asserts the two agree.
interface RegionMeta {
  /** Inclusive national-dex range [start, end]. */
  range: [number, number];
  /** Expected number of main-dex species (end - start + 1). */
  total: number;
}

export const REGION_META: Record<Exclude<PokedexRegionLite, "unknown">, RegionMeta> = {
  kanto: { range: [1, 151], total: 151 },
  johto: { range: [152, 251], total: 100 },
  hoenn: { range: [252, 386], total: 135 },
  sinnoh: { range: [387, 493], total: 107 },
  unova: { range: [494, 649], total: 156 },
  kalos: { range: [650, 721], total: 72 },
  alola: { range: [722, 809], total: 88 },
  galar: { range: [810, 898], total: 89 },
  hisui: { range: [899, 905], total: 7 },
  paldea: { range: [906, 1025], total: 120 },
  // Meltan #808 / Melmetal #809 — GO's "Unidentified" grouping (2 species).
  unidentified: { range: [808, 809], total: 2 },
};

export function expectedRegionTotal(region: PokedexRegionLite): number | null {
  if (region === "unknown") return null;
  return REGION_META[region].total;
}

// ─── Reading ─────────────────────────────────────────────────────────────────

export interface TopPanelVariantCounts {
  shiny?: number;
  lucky?: number;
  xxl?: number;
  xxs?: number;
  perfect?: number;
}

export interface TopPanelReading {
  /** Region read from the panel, or "unknown" if no region keyword matched. */
  region: PokedexRegionLite;
  /** Main "caught" count — the left number of `X / Y`. Null when unread. */
  mainCaught: number | null;
  /** Main "total" count — the right number of `X / Y`. Null when unread. */
  mainTotal: number | null;
  /** Variant stat counts when the expanded panel is present. */
  variants: TopPanelVariantCounts;
  /** 0..1 — how much we trust this reading. Region+count both present is high. */
  confidence: number;
  /** Human-readable note for diagnostics. */
  reason: string;
}

const REGION_TOKENS: Array<[Exclude<PokedexRegionLite, "unknown">, RegExp]> = [
  ["kanto", /\bkanto\b/i],
  ["johto", /\bjohto\b/i],
  ["hoenn", /\bhoenn\b/i],
  ["sinnoh", /\bsinnoh\b/i],
  ["unova", /\bunova\b/i],
  ["kalos", /\bkalos\b/i],
  ["alola", /\balola\b/i],
  ["galar", /\bgalar\b/i],
  ["hisui", /\bhisui\b/i],
  ["paldea", /\bpaldea\b/i],
];

function regionFromText(text: string): PokedexRegionLite {
  for (const [region, re] of REGION_TOKENS) {
    if (re.test(text)) return region;
  }
  return "unknown";
}

// `138 SHINY`, `150 LUCKY`, `99 XXL`, `91 XXS`, `72 100%`. The number sits to
// the LEFT of its label in the expanded panel. Each is optional.
const VARIANT_PATTERNS: Array<[keyof TopPanelVariantCounts, RegExp]> = [
  ["shiny", /(\d{1,4})\s*shiny\b/i],
  ["lucky", /(\d{1,4})\s*lucky\b/i],
  ["xxl", /(\d{1,4})\s*xxl\b/i],
  ["xxs", /(\d{1,4})\s*xxs\b/i],
  // "100%" or the plain "perfect" / "hundo" wording. No trailing \b — the "%"
  // is a non-word char so `%\b` would never match.
  ["perfect", /(\d{1,4})\s*(?:100\s*%|perfect\b|hundo\b)/i],
];

function parseVariants(text: string): TopPanelVariantCounts {
  const out: TopPanelVariantCounts = {};
  for (const [key, re] of VARIANT_PATTERNS) {
    const m = text.match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n >= 0) out[key] = n;
    }
  }
  return out;
}

/**
 * Parse the top-panel OCR text into a structured reading.
 *
 * Accepts the raw multi-line OCR string (region label + count + optional
 * expanded stat row). Tolerant of newlines, extra whitespace, and a missing
 * variant row.
 */
export function parseTopPanel(rawText: string): TopPanelReading {
  const text = (rawText ?? "").replace(/\s+/g, " ").trim();
  if (!text) {
    return {
      region: "unknown",
      mainCaught: null,
      mainTotal: null,
      variants: {},
      confidence: 0,
      reason: "empty",
    };
  }

  const region = regionFromText(text);

  // Main count: the FIRST `X / Y` pair. The variant row uses `N LABEL`, never a
  // slash, so the slash uniquely identifies the main count.
  const countMatch = text.match(/(\d{1,4})\s*\/\s*(\d{1,4})/);
  const mainCaught = countMatch ? parseInt(countMatch[1], 10) : null;
  const mainTotal = countMatch ? parseInt(countMatch[2], 10) : null;

  const variants = parseVariants(text);

  // Confidence: region + a valid main count is a strong, trustworthy reading.
  // Either alone is moderate. Neither is zero.
  let confidence = 0;
  const reasons: string[] = [];
  if (region !== "unknown") {
    confidence += 0.55;
    reasons.push(`region:${region}`);
  }
  if (mainCaught != null && mainTotal != null && mainTotal > 0 && mainCaught <= mainTotal) {
    confidence += 0.45;
    reasons.push(`count:${mainCaught}/${mainTotal}`);
  } else if (countMatch) {
    // A count was read but it is implausible (caught > total). Low trust.
    confidence += 0.1;
    reasons.push("count:implausible");
  }
  if (Object.keys(variants).length > 0) {
    reasons.push(`variants:${Object.keys(variants).join("+")}`);
  }

  return {
    region,
    mainCaught,
    mainTotal,
    variants,
    confidence: Math.min(1, confidence),
    reason: reasons.join(" "),
  };
}

// ─── Action decision ──────────────────────────────────────────────────────────

export type TopPanelAction =
  | "bulk_region_complete" // caught === total && matches expected region size
  | "narrow_missing" // partial: compute "find N missing"
  | "conflict" // panel total disagrees with expected region size
  | "ignored_low_confidence" // reading too weak to act on
  | "no_count"; // region known but no usable count

export interface TopPanelDecision {
  action: TopPanelAction;
  /** Region the panel resolved to (may override a stale guess). */
  region: PokedexRegionLite;
  /** Whether the panel region should override the caller's prior region guess. */
  overrideRegion: boolean;
  /** Caught/total echoed from the reading for diagnostics. */
  mainCaught: number | null;
  mainTotal: number | null;
  /** Expected remaining (total - caught) when narrowing; null otherwise. */
  missingExpected: number | null;
  variants: TopPanelVariantCounts;
  confidence: number;
  note: string;
}

const CONFIDENCE_FLOOR = 0.5;

/**
 * Decide what the app should do with a top-panel reading.
 *
 *   - Region override fires whenever the panel confidently names a region,
 *     even if the count is unusable — the visible header beats a stale tab
 *     guess (HISUI/Paldea) every time.
 *   - Bulk-complete fires only when caught === total AND total matches the
 *     app's expected region size, so a misread "15 / 15" can't wipe a region.
 *   - When caught < total we narrow to "find (total - caught) missing".
 *   - When the panel total disagrees with the expected region size we record a
 *     conflict and do NOT bulk-mark.
 */
export function decideTopPanelAction(reading: TopPanelReading): TopPanelDecision {
  const { region, mainCaught, mainTotal, variants, confidence } = reading;
  const expected = expectedRegionTotal(region);
  const overrideRegion = region !== "unknown" && confidence >= CONFIDENCE_FLOOR;

  const base = {
    region,
    overrideRegion,
    mainCaught,
    mainTotal,
    variants,
    confidence,
  };

  if (confidence < CONFIDENCE_FLOOR) {
    return { ...base, action: "ignored_low_confidence", missingExpected: null, note: "confidence below floor" };
  }

  if (mainCaught == null || mainTotal == null || mainTotal <= 0 || mainCaught > mainTotal) {
    return { ...base, action: "no_count", missingExpected: null, note: "region resolved but no usable count" };
  }

  // Panel total must agree with the app's expected region size before we trust
  // it for bulk decisions. A mismatch (e.g. OCR read "15/15" for Kanto) is a
  // conflict, not a completion.
  if (expected != null && mainTotal !== expected) {
    return {
      ...base,
      action: "conflict",
      missingExpected: null,
      note: `panel total ${mainTotal} != expected ${expected} for ${region}`,
    };
  }

  if (mainCaught === mainTotal) {
    return {
      ...base,
      action: "bulk_region_complete",
      missingExpected: 0,
      note: `all ${mainTotal} caught in ${region}`,
    };
  }

  const missing = mainTotal - mainCaught;
  return {
    ...base,
    action: "narrow_missing",
    missingExpected: missing,
    note: `find ${missing} missing in ${region} (${mainCaught}/${mainTotal})`,
  };
}

/**
 * Build the bulk "present" observations for a completed region, ready to feed
 * to dexStateStore.applyScanObservations (which already protects manual
 * overrides — the safe union semantics the brief requires).
 *
 * Only valid for `bulk_region_complete`; returns [] otherwise so a caller can
 * blindly map a decision to observations.
 */
export function topPanelBulkObservations(
  decision: TopPanelDecision,
): Array<{ dexNumber: number; state: CellState }> {
  if (decision.action !== "bulk_region_complete") return [];
  if (decision.region === "unknown") return [];
  const meta = REGION_META[decision.region];
  const out: Array<{ dexNumber: number; state: CellState }> = [];
  for (let n = meta.range[0]; n <= meta.range[1]; n++) {
    out.push({ dexNumber: n, state: "present" });
  }
  return out;
}

// ─── Diagnostics projection ────────────────────────────────────────────────────
// Shape the session/diagnostics JSON should carry when a top panel is read.
export interface TopPanelDiagnostics {
  top_panel_region: PokedexRegionLite;
  top_panel_main_count: string | null; // "151 / 151"
  top_panel_missing_expected: number | null;
  top_panel_action: TopPanelAction;
  top_panel_variants?: TopPanelVariantCounts;
  top_panel_confidence: number;
}

export function topPanelDiagnostics(decision: TopPanelDecision): TopPanelDiagnostics {
  const count =
    decision.mainCaught != null && decision.mainTotal != null
      ? `${decision.mainCaught} / ${decision.mainTotal}`
      : null;
  return {
    top_panel_region: decision.region,
    top_panel_main_count: count,
    top_panel_missing_expected: decision.missingExpected,
    top_panel_action: decision.action,
    ...(Object.keys(decision.variants).length > 0 ? { top_panel_variants: decision.variants } : {}),
    top_panel_confidence: Math.round(decision.confidence * 100) / 100,
  };
}
